import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  Timestamp,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import {
  UserDocument,
  CreateUserDocumentData,
  UpdateUserDocumentData,
  AccountData,
  GlobalSettings,
  OpenPosition,
  OpenOrder,
  ClosedTrade,
  TradingHistoryEntry,
  AITradingData,
  ManualTradingData,
  TradingPanelConnection,
  FinancialTransaction,
  AccountSummary,
  TradingSettings,
  RiskManagement,
  PerformanceMetrics,
  UserDocumentFilters
} from '../types/userDocument';

export class UserDocumentService {
  private static readonly COLLECTION_NAME = 'userDocuments';

  /**
   * Check if user is authenticated
   */
  private static checkAuthentication(): string {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to access user documents');
    }
    return user.uid;
  }

  /**
   * The key an account lives under in the document.
   *
   * The interface calls the funded account "live" and the document has always called it
   * "real". Most callers translate before they get here, but not all of them did, and the
   * ones that did not were writing to `accounts.live` — which does not exist, so every trade
   * opened, closed or amended on the live account threw. Translating on the way in means no
   * caller has to know the document's spelling.
   */
  private static accountKey(accountType: string): string {
    return accountType === 'live' ? 'real' : accountType;
  }

  /**
   * Create default account data for a specific account type
   */
  private static createDefaultAccountData(accountType: string, now: Date): AccountData {
    return {
      // Initialize Account Summary with default values
      accountSummary: {
        balance: {
          totalBalance: accountType === 'demo' ? 1000 : 0, // Demo starts with $1,000
          availableBalance: accountType === 'demo' ? 1000 : 0,
          lockedBalance: 0,
          currency: 'USD'
        },
        pnl: {
          totalPnL: 0,
          dailyPnL: 0,
          weeklyPnL: 0,
          monthlyPnL: 0,
          yearlyPnL: 0,
          unrealizedPnL: 0,
          realizedPnL: 0
        },
        margin: {
          freeMargin: accountType === 'demo' ? 1000 : 0,
          usedMargin: 0,
          marginLevel: 0,
          marginCallLevel: 100,
          stopOutLevel: 20
        },
        equity: {
          totalEquity: accountType === 'demo' ? 1000 : 0,
          freeEquity: accountType === 'demo' ? 1000 : 0,
          usedEquity: 0
        },
        leverage: {
          currentLeverage: 100,
          maxLeverage: 100,
          leverageHistory: []
        },
        deposits: {
          totalDeposits: 0,
          pendingDeposits: 0,
          depositMethods: [],
          depositHistory: []
        },
        withdrawals: {
          totalWithdrawals: 0,
          pendingWithdrawals: 0,
          withdrawalMethods: [],
          withdrawalHistory: []
        },
        accountStatus: {
          status: 'active',
          kycStatus: accountType === 'demo' ? 'not_required' : 'not_submitted',
          tradingEnabled: true,
          withdrawalEnabled: accountType === 'demo' ? false : false,
          depositEnabled: accountType === 'demo' ? false : true
        },
        statistics: {
          totalTrades: 0,
          winningTrades: 0,
          losingTrades: 0,
          winRate: 0,
          averageWin: 0,
          averageLoss: 0,
          largestWin: 0,
          largestLoss: 0,
          profitFactor: 0,
          sharpeRatio: 0,
          maxDrawdown: 0,
          maxDrawdownPercent: 0
        }
      },

      // Initialize empty arrays for trading data
      openPositions: [],
      openOrders: [],
      closedTrades: [],
      tradingHistory: [],
      financialTransactions: [],

      // Initialize AI Trading Data with new per-asset structure
      aiTradingData: {
        globalEnabled: false,
        globalSettings: {
          maxConcurrentAssets: 5, // Allow up to 5 assets trading simultaneously
          globalRiskLimit: 10, // Global risk limit percentage
          globalDailyLoss: 20, // Global daily loss limit percentage
          emergencyStopEnabled: true
        },
        assets: {}, // Per-asset configurations will be added as assets are configured
        globalPerformance: {
          totalTrades: 0,
          winningTrades: 0,
          losingTrades: 0,
          winRate: 0,
          totalPnL: 0,
          averageTradeDuration: 0,
          sharpeRatio: 0,
          maxDrawdown: 0,
          activeAssets: []
        },
        lastUpdated: now,
        
        // Backward compatibility fields
        enabled: false,
        strategies: [],
        activeStrategies: [],
        performance: {
          totalTrades: 0,
          winningTrades: 0,
          losingTrades: 0,
          winRate: 0,
          totalPnL: 0,
          averageTradeDuration: 0,
          sharpeRatio: 0,
          maxDrawdown: 0
        },
        settings: {
          maxPositions: 5,
          maxRiskPerTrade: 2,
          maxDailyLoss: 10,
          autoStopLoss: true,
          autoTakeProfit: true,
          riskManagementEnabled: true
        }
      },

      // Initialize Manual Trading Data
      manualTradingData: {
        preferences: {
          defaultLeverage: 1,
          defaultStopLoss: 0,
          defaultTakeProfit: 0,
          confirmBeforeTrade: true,
          soundNotifications: true,
          emailNotifications: true,
          pushNotifications: true
        },
        shortcuts: {
          quickBuy: false,
          quickSell: false,
          oneClickTrading: false,
          keyboardShortcuts: {}
        },
        lastUpdated: now
      },

      // Initialize Trading Settings
      tradingSettings: {
        general: {
          defaultLeverage: 1,
          defaultStopLoss: 0,
          defaultTakeProfit: 0,
          confirmBeforeTrade: true,
          showTradeConfirmation: true
        },
        notifications: {
          tradeExecuted: true,
          tradeClosed: true,
          marginCall: true,
          stopLossHit: true,
          takeProfitHit: true,
          emailNotifications: true,
          pushNotifications: true,
          soundNotifications: true
        },
        display: {
          theme: 'auto',
          language: 'en',
          timezone: 'UTC',
          dateFormat: 'YYYY-MM-DD',
          numberFormat: 'en-US',
          priceDisplayMode: 'mid'
        },
        risk: {
          maxPositions: 10,
          maxRiskPerTrade: 5,
          maxDailyLoss: 20,
          autoCloseOnMarginCall: true,
          autoCloseOnStopOut: true
        }
      },

      // Initialize Risk Management
      riskManagement: {
        enabled: true,
        maxDrawdown: 20,
        maxDailyLoss: 10,
        maxPositions: 10,
        maxRiskPerTrade: 5,
        emergencyStopLoss: false,
        autoCloseOnMarginCall: true,
        autoCloseOnStopOut: true,
        lastUpdated: now
      },

      // Initialize Performance Metrics
      performanceMetrics: {
        overall: {
          totalReturn: 0,
          annualizedReturn: 0,
          sharpeRatio: 0,
          sortinoRatio: 0,
          calmarRatio: 0,
          maxDrawdown: 0,
          maxDrawdownPercent: 0,
          winRate: 0,
          profitFactor: 0,
          averageWin: 0,
          averageLoss: 0,
          largestWin: 0,
          largestLoss: 0
        },
        monthly: [],
        yearly: [],
        lastUpdated: now
      },

      // Account metadata
      accountMetadata: {
        accountType: accountType,
        createdAt: now,
        lastActive: now,
        isActive: true
      }
    };
  }

  /**
   * Create a new user document with default values
   */
  static async createUserDocument(data: CreateUserDocumentData): Promise<UserDocument> {
    try {
      // Verify authentication
      const currentUserId = this.checkAuthentication();
      
      // Ensure the userId matches the authenticated user
      if (data.userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot create document for different user');
      }
      const now = new Date();
      
      const userDocument: UserDocument = {
        userId: data.userId,
        email: data.email,
        displayName: data.displayName,
        createdAt: now,
        lastUpdated: now,

        // Initialize accounts with demo and real accounts
        accounts: {
          demo: this.createDefaultAccountData('demo', now),
          real: this.createDefaultAccountData('real', now)
        },

        // Initialize global settings
        globalSettings: {
          display: {
            theme: 'auto',
            language: 'en',
            timezone: 'UTC',
            dateFormat: 'YYYY-MM-DD',
            numberFormat: 'en-US'
          },
          notifications: {
            emailNotifications: true,
            pushNotifications: true,
            soundNotifications: true
          },
          defaultSettings: {
            defaultLeverage: 1,
            defaultStopLoss: 0,
            defaultTakeProfit: 0,
            confirmBeforeTrade: true
          }
        },

        // Initialize trading panel connections (global)
        tradingPanelConnections: []
      };

      // Convert dates to Firestore timestamps
      const firestoreDocument = this.convertDatesToTimestamps(userDocument);
      
      
      await setDoc(doc(db, this.COLLECTION_NAME, data.userId), firestoreDocument);
      
      
      return userDocument;
    } catch (error) {
      console.error('Error creating user document:', error);
      throw new Error('Failed to create user document');
    }
  }

  /**
   * Get user document by user ID
   */
  static async getUserDocument(userId: string): Promise<UserDocument | null> {
    try {
      // Verify authentication
      const currentUserId = this.checkAuthentication();
      
      // Ensure the userId matches the authenticated user
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot access document for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const rawData = docSnap.data() as any;
        const convertedData = this.convertTimestampsToDates(rawData) as UserDocument;
        
        //   hasAccounts: !!convertedData.accounts,
        //   hasDemoAccount: !!convertedData.accounts?.demo,
        //   hasRealAccount: !!convertedData.accounts?.real,
        //   demoBalance: convertedData.accounts?.demo?.accountSummary?.balance?.totalBalance,
        //   realBalance: convertedData.accounts?.real?.accountSummary?.balance?.totalBalance
        // });
        
        return convertedData;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user document:', error);
      throw new Error('Failed to get user document');
    }
  }

  /**
   * Update user document
   */
  static async updateUserDocument(userId: string, updates: UpdateUserDocumentData): Promise<void> {
    try {
      // Verify authentication
      const currentUserId = this.checkAuthentication();
      
      // Ensure the userId matches the authenticated user
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot update document for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Add lastUpdated timestamp
      const updateData = {
        ...updates,
        lastUpdated: serverTimestamp()
      };
      
      // Convert dates to timestamps
      const firestoreUpdates = this.convertDatesToTimestamps(updateData);
      
      await updateDoc(docRef, firestoreUpdates);
    } catch (error) {
      console.error('Error updating user document:', error);
      throw new Error('Failed to update user document');
    }
  }

  /**
   * Add or update open position for specific account
   */
  static async updateOpenPosition(userId: string, uiAccountType: string, position: OpenPosition): Promise<void> {
    const accountType = this.accountKey(uiAccountType);
    try {
      const currentUserId = this.checkAuthentication();
      
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot update position for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc.accounts?.[accountType];
      
      if (!accountData) {
        throw new Error(`Account type '${accountType}' not found`);
      }
      
      const openPositions = accountData.openPositions || [];
      
      // Find existing position or add new one
      const existingIndex = openPositions.findIndex((p: any) => p.id === position.id);
      
      if (existingIndex >= 0) {
        openPositions[existingIndex] = position;
      } else {
        openPositions.push(position);
      }
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date();
      
      await updateDoc(docRef, {
        [`accounts.${accountType}.openPositions`]: this.convertDatesToTimestamps(openPositions),
        [`accounts.${accountType}.accountMetadata.lastActive`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating open position:', error);
      throw new Error('Failed to update open position');
    }
  }

  /**
   * Remove open position from specific account
   */
  static async removeOpenPosition(userId: string, uiAccountType: string, positionId: string): Promise<void> {
    const accountType = this.accountKey(uiAccountType);
    try {
      const currentUserId = this.checkAuthentication();
      
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot remove position for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc.accounts?.[accountType];
      
      if (!accountData) {
        throw new Error(`Account type '${accountType}' not found`);
      }
      
      const openPositions = accountData.openPositions || [];
      const filteredPositions = openPositions.filter((p: any) => p.id !== positionId);
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date();
      
      await updateDoc(docRef, {
        [`accounts.${accountType}.openPositions`]: this.convertDatesToTimestamps(filteredPositions),
        [`accounts.${accountType}.accountMetadata.lastActive`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error removing open position:', error);
      throw new Error('Failed to remove open position');
    }
  }

  /**
   * Add or update open order for specific account
   */
  static async updateOpenOrder(userId: string, uiAccountType: string, order: OpenOrder): Promise<void> {
    const accountType = this.accountKey(uiAccountType);
    try {
      const currentUserId = this.checkAuthentication();
      
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot update order for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc.accounts?.[accountType];
      
      if (!accountData) {
        throw new Error(`Account type '${accountType}' not found`);
      }
      
      const openOrders = accountData.openOrders || [];
      
      // Find existing order or add new one
      const existingIndex = openOrders.findIndex((o: any) => o.id === order.id);
      
      if (existingIndex >= 0) {
        openOrders[existingIndex] = order;
      } else {
        openOrders.push(order);
      }
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date();
      
      await updateDoc(docRef, {
        [`accounts.${accountType}.openOrders`]: this.convertDatesToTimestamps(openOrders),
        [`accounts.${accountType}.accountMetadata.lastActive`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating open order:', error);
      throw new Error('Failed to update open order');
    }
  }

  /**
   * Remove open order from specific account
   */
  static async removeOpenOrder(userId: string, uiAccountType: string, orderId: string): Promise<void> {
    const accountType = this.accountKey(uiAccountType);
    try {
      const currentUserId = this.checkAuthentication();
      
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot remove order for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc.accounts?.[accountType];
      
      if (!accountData) {
        throw new Error(`Account type '${accountType}' not found`);
      }
      
      const openOrders = accountData.openOrders || [];
      const filteredOrders = openOrders.filter((o: any) => o.id !== orderId);
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date();
      
      await updateDoc(docRef, {
        [`accounts.${accountType}.openOrders`]: this.convertDatesToTimestamps(filteredOrders),
        [`accounts.${accountType}.accountMetadata.lastActive`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error removing open order:', error);
      throw new Error('Failed to remove open order');
    }
  }

  /**
   * Add closed trade to specific account
   */
  static async addClosedTrade(userId: string, uiAccountType: string, trade: ClosedTrade): Promise<void> {
    const accountType = this.accountKey(uiAccountType);
    try {
      const currentUserId = this.checkAuthentication();
      
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot add trade for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc.accounts?.[accountType];
      
      if (!accountData) {
        throw new Error(`Account type '${accountType}' not found`);
      }
      
      const closedTrades = accountData.closedTrades || [];
      closedTrades.push(trade);
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date();
      
      await updateDoc(docRef, {
        [`accounts.${accountType}.closedTrades`]: this.convertDatesToTimestamps(closedTrades),
        [`accounts.${accountType}.accountMetadata.lastActive`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error adding closed trade:', error);
      throw new Error('Failed to add closed trade');
    }
  }

  /**
   * Add trading history entry to specific account
   */
  static async addTradingHistoryEntry(userId: string, uiAccountType: string, entry: TradingHistoryEntry): Promise<void> {
    const accountType = this.accountKey(uiAccountType);
    try {
      const currentUserId = this.checkAuthentication();
      
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot add history for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc.accounts?.[accountType];
      
      if (!accountData) {
        throw new Error(`Account type '${accountType}' not found`);
      }
      
      const tradingHistory = accountData.tradingHistory || [];
      tradingHistory.push(entry);
      
      // Keep only last 1000 entries to prevent document from growing too large
      if (tradingHistory.length > 1000) {
        tradingHistory.splice(0, tradingHistory.length - 1000);
      }
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date();
      
      await updateDoc(docRef, {
        [`accounts.${accountType}.tradingHistory`]: this.convertDatesToTimestamps(tradingHistory),
        [`accounts.${accountType}.accountMetadata.lastActive`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error adding trading history entry:', error);
      throw new Error('Failed to add trading history entry');
    }
  }

  /**
   * Update account summary for specific account
   */
  static async updateAccountSummary(userId: string, uiAccountType: string, summary: Partial<AccountSummary>): Promise<void> {
    const accountType = this.accountKey(uiAccountType);
    try {
      const currentUserId = this.checkAuthentication();
      
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot update summary for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc.accounts?.[accountType];
      
      if (!accountData) {
        throw new Error(`Account type '${accountType}' not found`);
      }
      
      const currentSummary = accountData.accountSummary || {};
      const updatedSummary = {
        ...currentSummary,
        ...summary
      };
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date();
      
      await updateDoc(docRef, {
        [`accounts.${accountType}.accountSummary`]: this.convertDatesToTimestamps(updatedSummary),
        [`accounts.${accountType}.accountMetadata.lastActive`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating account summary:', error);
      throw new Error('Failed to update account summary');
    }
  }

  /**
   * Update AI trading data for specific account
   */
  static async updateAITradingData(userId: string, uiAccountType: string, aiData: Partial<AITradingData>): Promise<void> {
    const accountType = this.accountKey(uiAccountType);
    try {
      const currentUserId = this.checkAuthentication();
      
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot update AI data for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc.accounts?.[accountType];
      
      if (!accountData) {
        throw new Error(`Account type '${accountType}' not found`);
      }
      
      const currentAiData = accountData.aiTradingData || {};
      const updatedAiData = {
        ...currentAiData,
        ...aiData,
        lastUpdated: new Date()
      };
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date();
      
      await updateDoc(docRef, {
        [`accounts.${accountType}.aiTradingData`]: this.convertDatesToTimestamps(updatedAiData),
        [`accounts.${accountType}.accountMetadata.lastActive`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating AI trading data:', error);
      throw new Error('Failed to update AI trading data');
    }
  }

  /**
   * Update manual trading data for specific account
   */
  static async updateManualTradingData(userId: string, accountType: string, manualData: Partial<ManualTradingData>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const currentManualData = userDoc.manualTradingData || {};
      
      const updatedManualData = {
        ...currentManualData,
        ...manualData,
        lastUpdated: new Date()
      };
      
      await updateDoc(docRef, {
        manualTradingData: this.convertDatesToTimestamps(updatedManualData),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating manual trading data:', error);
      throw new Error('Failed to update manual trading data');
    }
  }

  /**
   * Add financial transaction to specific account
   */
  static async addFinancialTransaction(userId: string, accountType: string, transaction: FinancialTransaction): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const financialTransactions = userDoc.financialTransactions || [];
      
      financialTransactions.push(transaction);
      
      await updateDoc(docRef, {
        financialTransactions: this.convertDatesToTimestamps(financialTransactions),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error adding financial transaction:', error);
      throw new Error('Failed to add financial transaction');
    }
  }

  /**
   * Update trading settings for specific account
   */
  static async updateTradingSettings(userId: string, accountType: string, settings: Partial<TradingSettings>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const currentSettings = userDoc.tradingSettings || {};
      
      const updatedSettings = {
        ...currentSettings,
        ...settings
      };
      
      await updateDoc(docRef, {
        tradingSettings: this.convertDatesToTimestamps(updatedSettings),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating trading settings:', error);
      throw new Error('Failed to update trading settings');
    }
  }

  /**
   * Update risk management settings
   */
  static async updateRiskManagement(userId: string, accountType: string, riskManagement: Partial<RiskManagement>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const currentRiskManagement = userDoc.riskManagement || {};
      
      const updatedRiskManagement = {
        ...currentRiskManagement,
        ...riskManagement,
        lastUpdated: new Date()
      };
      
      await updateDoc(docRef, {
        riskManagement: this.convertDatesToTimestamps(updatedRiskManagement),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating risk management:', error);
      throw new Error('Failed to update risk management');
    }
  }

  /**
   * Update performance metrics
   */
  static async updatePerformanceMetrics(userId: string, accountType: string, metrics: Partial<PerformanceMetrics>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userDoc = docSnap.data() as any;
      const currentMetrics = userDoc.performanceMetrics || {};
      
      const updatedMetrics = {
        ...currentMetrics,
        ...metrics,
        lastUpdated: new Date()
      };
      
      await updateDoc(docRef, {
        performanceMetrics: this.convertDatesToTimestamps(updatedMetrics),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating performance metrics:', error);
      throw new Error('Failed to update performance metrics');
    }
  }

  /**
   * Delete user document
   */
  static async deleteUserDocument(userId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting user document:', error);
      throw new Error('Failed to delete user document');
    }
  }

  /**
   * Check if user document exists
   */
  static async userDocumentExists(userId: string): Promise<boolean> {
    try {
      // Verify authentication
      const currentUserId = this.checkAuthentication();
      
      // Ensure the userId matches the authenticated user
      if (userId !== currentUserId) {
        throw new Error('User ID mismatch: Cannot check document for different user');
      }
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists();
    } catch (error) {
      console.error('Error checking user document existence:', error);
      return false;
    }
  }

  /**
   * Convert JavaScript dates to Firestore timestamps
   */
  private static convertDatesToTimestamps(obj: any): any {
    if (obj === null || obj === undefined) {
      return null; // Convert undefined to null for Firestore compatibility
    }
    
    if (obj instanceof Date) {
      return Timestamp.fromDate(obj);
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.convertDatesToTimestamps(item)).filter(item => item !== undefined);
    }
    
    if (typeof obj === 'object') {
      const converted: any = {};
      for (const key in obj) {
        const convertedValue = this.convertDatesToTimestamps(obj[key]);
        if (convertedValue !== undefined) {
          converted[key] = convertedValue;
        }
      }
      return converted;
    }
    
    return obj;
  }

  /**
   * Convert Firestore timestamps to JavaScript dates
   */
  private static convertTimestampsToDates(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (obj instanceof Timestamp) {
      return obj.toDate();
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.convertTimestampsToDates(item));
    }
    
    if (typeof obj === 'object') {
      const converted: any = {};
      for (const key in obj) {
        converted[key] = this.convertTimestampsToDates(obj[key]);
      }
      return converted;
    }
    
    return obj;
  }

  /**
   * Get user documents with pagination
   */
  static async getUserDocuments(
    pageSize: number = 10,
    lastDoc?: QueryDocumentSnapshot
  ): Promise<{ documents: UserDocument[]; lastDoc?: QueryDocumentSnapshot }> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy('createdAt', 'desc'),
        limit(pageSize),
        ...(lastDoc ? [startAfter(lastDoc)] : [])
      );
      
      const querySnapshot = await getDocs(q);
      const documents = querySnapshot.docs.map(doc => 
        this.convertTimestampsToDates(doc.data()) as UserDocument
      );
      
      const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      return {
        documents,
        lastDoc: newLastDoc
      };
    } catch (error) {
      console.error('Error getting user documents:', error);
      throw new Error('Failed to get user documents');
    }
  }

  /**
   * Batch update multiple fields
   */
  static async batchUpdate(userId: string, updates: Record<string, any>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      const updateData = {
        ...updates,
        lastUpdated: serverTimestamp()
      };
      
      const firestoreUpdates = this.convertDatesToTimestamps(updateData);
      
      await updateDoc(docRef, firestoreUpdates);
    } catch (error) {
      console.error('Error batch updating user document:', error);
      throw new Error('Failed to batch update user document');
    }
  }
}
